/**
 * Handing transcription seats to clinicians, from the pricing page.
 *
 * Opened straight after a purchase, because seats bought there belong to the practice and nobody
 * holds one yet - a practice left at "seats added" has paid for something that does nothing until
 * somebody is put on it.
 *
 * Two server rules shape what follows. A seat is applied on the click rather than batched, because
 * the server answers each change with the whole table and that reply is what the rows finally
 * render from. Free trials sit outside all of it: they are granted automatically, hold no seat,
 * and the server refuses both assigning and releasing one.
 *
 * The assignment view model is deliberately close to transcriptionSeatsController's ASSIGN section
 * rather than shared with it: that screen also carries buying, usage and a wallet link, none of
 * which belong in a modal opened from a public page. If a third caller ever needs this table, it
 * is worth lifting into a component both can use.
 */
app.controller("seatAssignController", [
    "$scope",
    "$q",
    "$timeout",
    "$uibModalInstance",
    "TranscriptionSeatsAPI",
    "Session",
    "USER_ROLE",
    function ($scope, $q, $timeout, $uibModalInstance, TranscriptionSeatsAPI, Session, USER_ROLE) {
        $scope.Session = Session;
        $scope.API = TranscriptionSeatsAPI;
        $scope.TIER = TranscriptionSeatsAPI.TIER;

        /** Held as one stable array so ng-repeat is not handed a fresh literal on every digest. */
        $scope.TIERS = [TranscriptionSeatsAPI.TIER.STANDARD, TranscriptionSeatsAPI.TIER.PREMIUM];

        $scope.getTierLabel = TranscriptionSeatsAPI.getTierLabel;
        $scope.isTrialTier = TranscriptionSeatsAPI.isTrialTier;
        $scope.formatSeconds = TranscriptionSeatsAPI.formatSeconds;

        $scope.isLoading = true;
        $scope.loadError = null;
        $scope.errorMessage = null;
        $scope.successMessage = null;
        $scope.searchText = "";

        $scope.subscription = null;
        $scope.usage = null;
        $scope.users = [];

        /**
         * The tier each user is on, keyed by user ID. Null means no seat. Moved the moment a tier
         * is clicked so the row and the availability counts answer straight away, and put back if
         * the server refuses the change.
         */
        $scope.assignedTier = {};

        /** The user whose seat is in flight, so their row can show progress. */
        $scope.savingUserId = null;

        /** The row order, keyed by user ID, so a row cannot jump out from under the cursor. */
        var sortRanks = {};

        // =================================================================
        // Which practice
        // =================================================================

        /**
         * Seats are owned by one practice at a time, so a group works through its practices one
         * after another. Read from the session rather than fetched: the purchase modal has already
         * loaded them, and it is the only way into this one.
         */
        $scope.practices = Session.practiceOptions || [];
        $scope.isChoosingPractice = false;

        $scope.hasPracticeChoice = function () {
            return $scope.practices.length > 1;
        };

        $scope.changePractice = function () {
            $scope.isChoosingPractice = true;
        };

        $scope.cancelPracticeChange = function () {
            $scope.isChoosingPractice = false;
        };

        $scope.choosePractice = function (practice) {
            $scope.isChoosingPractice = false;

            if (Session.practice && practice.id === Session.practice.id) {
                return null;
            }

            Session.setPractice(practice);

            // The ranks describe the practice being left, and a user ID means a different row now.
            sortRanks = {};
            $scope.users = [];
            $scope.assignedTier = {};
            $scope.subscription = null;
            $scope.errorMessage = null;
            $scope.successMessage = null;
            $scope.searchText = "";

            return load();
        };

        // =================================================================
        // Loading
        // =================================================================

        function load() {
            $scope.isLoading = true;
            $scope.loadError = null;

            return $q
                .all({
                    subscription: TranscriptionSeatsAPI.getSubscription(),
                    usage: TranscriptionSeatsAPI.getSeatUsage(null),
                })
                .then(function (responses) {
                    $scope.subscription = responses.subscription.data || null;
                    applyUsage(responses.usage.data);
                })
                .catch(function (error) {
                    $scope.loadError = TranscriptionSeatsAPI.getErrorMessage(
                        error,
                        "Could not load your team. Please try again shortly."
                    );
                })
                .finally(function () {
                    $timeout(function () {
                        $scope.isLoading = false;
                    });
                });
        }

        /**
         * Takes the server's usage payload as the single source of truth, so the table always ends
         * up rendering from the server rather than from the tier a click applied optimistically.
         */
        function applyUsage(usage) {
            $scope.usage = usage || null;
            $scope.users = (usage && usage.users) || [];
            $scope.assignedTier = {};

            $scope.users.forEach(function (user) {
                var tier = user.seat_tier || null;

                $scope.assignedTier[user.user_id] = tier;

                // Trials sit between paid seats and unseated users, being the rows most likely to
                // need a seat. Held across reloads so the table does not reshuffle on every click.
                if (sortRanks[user.user_id] == null) {
                    sortRanks[user.user_id] =
                        user.is_active_in_practice === false ? 0 : TranscriptionSeatsAPI.isTrialTier(tier) ? 2 : tier ? 1 : 3;
                }

                user.sortRank = sortRanks[user.user_id];

                // Stamped on the row rather than read through a scope call, so the labels are
                // worked out once per load instead of on every digest the filtered table runs.
                user.roleLabel = formatRoles(user.roles);
            });
        }

        // =================================================================
        // Reading the subscription
        // =================================================================

        $scope.getAllocation = function (tier) {
            return TranscriptionSeatsAPI.getAllocation($scope.subscription, tier);
        };

        $scope.isSubscriptionLive = function () {
            return TranscriptionSeatsAPI.isSubscriptionLive($scope.subscription);
        };

        $scope.hasSubscription = function () {
            return !!$scope.subscription && $scope.subscription.status !== "NONE";
        };

        // =================================================================
        // Seat availability
        // =================================================================

        /**
         * Counts what the rows are showing rather than the last subscription payload, so the
         * remaining count is already right while a change is still in flight.
         */
        $scope.getAssignedCount = function (tier) {
            return $scope.users.filter(function (user) {
                return $scope.assignedTier[user.user_id] === tier;
            }).length;
        };

        $scope.getAvailableCount = function (tier) {
            return $scope.getAllocation(tier).quantity_owned - $scope.getAssignedCount(tier);
        };

        $scope.isEditable = function () {
            return !!$scope.usage && $scope.usage.is_current_period !== false && $scope.isSubscriptionLive();
        };

        // =================================================================
        // Assigning
        // =================================================================

        /** Whether the server currently has this user on their one free trial. */
        $scope.isTrialHolder = function (user) {
            return TranscriptionSeatsAPI.isTrialTier(user.seat_tier);
        };

        /**
         * Whether a row may be moved onto a tier. An orphaned seat can be released but never
         * re-assigned, because its holder is no longer a member of the practice.
         */
        $scope.canAssign = function (user, tier) {
            if (!$scope.isEditable()) {
                return false;
            }

            // Seats move one at a time: every change comes back with the whole table, so a second
            // click sent before the first has answered would only be re-rendered away.
            if (TranscriptionSeatsAPI.isProcessing) {
                return false;
            }

            // A trial is granted automatically and expires on its own, so the server refuses both
            // assigning one and releasing one. Its slot is shown filled but is never offered.
            if (TranscriptionSeatsAPI.isTrialTier(tier)) {
                return false;
            }

            if (!tier) {
                return !$scope.isTrialHolder(user);
            }

            if (user.is_active_in_practice === false) {
                return false;
            }

            if ($scope.assignedTier[user.user_id] === tier) {
                return true;
            }

            return $scope.getAvailableCount(tier) > 0;
        };

        /**
         * Applies a tier the moment it is clicked. The row moves first so the click feels answered,
         * then the server's reply is what the table finally renders from.
         */
        $scope.setTier = function (user, tier) {
            var previous = $scope.assignedTier[user.user_id] || null;

            if (!$scope.canAssign(user, tier) || previous === (tier || null)) {
                return;
            }

            $scope.assignedTier[user.user_id] = tier;
            $scope.savingUserId = user.user_id;
            $scope.errorMessage = null;
            $scope.successMessage = null;

            applyAssignment(user, tier, previous);
        };

        /**
         * Sends the one change on its own. A refusal puts the row back where it was, so the screen
         * never shows a seat the practice does not actually hold.
         */
        function applyAssignment(user, tier, previous) {
            TranscriptionSeatsAPI.isProcessing = true;

            return TranscriptionSeatsAPI.updateSeatAssignments([{ user_id: user.user_id, tier: tier }])
                .then(
                    function (response) {
                        applyUsage(response.data);

                        $scope.successMessage = tier
                            ? $scope.getFullName(user) + " now has a " + $scope.getTierLabel(tier).toLowerCase() + " seat."
                            : $scope.getFullName(user) + " no longer has a seat.";

                        $timeout(function () {
                            $scope.successMessage = null;
                        }, 5000);

                        // The assigned counts have moved, so the header must be re-read too.
                        // Failing that only leaves the counts stale, so it does not undo the row.
                        return TranscriptionSeatsAPI.getSubscription()
                            .then(function (subscriptionResponse) {
                                $scope.subscription = subscriptionResponse.data || $scope.subscription;
                            })
                            .catch(angular.noop);
                    },
                    function (error) {
                        $scope.assignedTier[user.user_id] = previous;
                        $scope.errorMessage = TranscriptionSeatsAPI.getErrorMessage(error, "Could not update that seat. Please try again.");
                    }
                )
                .finally(function () {
                    $timeout(function () {
                        $scope.savingUserId = null;
                        TranscriptionSeatsAPI.isProcessing = false;
                    });
                });
        }

        // =================================================================
        // Presentation
        // =================================================================

        $scope.getFullName = function (user) {
            return ((user.first_name || "") + " " + (user.last_name || "")).trim() || user.email || "User " + user.user_id;
        };

        /**
         * Turns the server's comma separated role codes into the labels the rest of the app uses,
         * so a row reads "Practice Admin" rather than "PRACTICE_ADMIN". A code the constant does
         * not carry is spelled out from the code itself rather than dropped, because an unlisted
         * role still tells the practice something about who a seat is going to.
         */
        function formatRoles(roles) {
            if (!roles) {
                return null;
            }

            return roles
                .split(",")
                .map(function (role) {
                    return role.trim();
                })
                .filter(function (role) {
                    return !!role;
                })
                .map(function (role) {
                    var known = USER_ROLE.find(function (option) {
                        return option.id === role;
                    });

                    return known ? known.label : titleCase(role);
                })
                .join(", ");
        }

        function titleCase(value) {
            return value
                .toLowerCase()
                .split("_")
                .map(function (word) {
                    return word.charAt(0).toUpperCase() + word.slice(1);
                })
                .join(" ");
        }

        /**
         * Narrows the table by name, email or role. Written out rather than left to Angular's
         * default filter, which would also match the internal sort key held on each row.
         */
        $scope.userFilter = function (user) {
            if (!$scope.searchText) {
                return true;
            }

            var needle = $scope.searchText.toLowerCase();

            return (
                $scope.getFullName(user).toLowerCase().indexOf(needle) !== -1 ||
                (user.email || "").toLowerCase().indexOf(needle) !== -1 ||
                (user.roleLabel || "").toLowerCase().indexOf(needle) !== -1
            );
        };

        /** Paid seats only - a trial costs the practice nothing and holds no seat. */
        $scope.getSeatedUserCount = function () {
            return $scope.users.filter(function (user) {
                return !!user.seat_tier && !TranscriptionSeatsAPI.isTrialTier(user.seat_tier);
            }).length;
        };

        $scope.getTrialUserCount = function () {
            return $scope.users.filter(function (user) {
                return TranscriptionSeatsAPI.isTrialTier(user.seat_tier);
            }).length;
        };

        $scope.close = function () {
            $uibModalInstance.dismiss("done");
        };

        /** Sends the caller back to the purchase modal, for a practice that has run out of seats. */
        $scope.buyMoreSeats = function () {
            $uibModalInstance.close({ action: "BUY_SEATS" });
        };

        load();
    },
]);
